/**
 * The vaccination tracking plan. Mirrors the "Vaccinations Tracking" datasource in Storyblok
 * (slug `vaccinations-tracking`), which analytics owns — keep the two in sync, values verbatim.
 *
 * Naming: `vaccination.<page>.<category>.<action>`. `vaccine_card` is the one exception: that CTA
 * sits on a card rendered on three different pages, so the page travels in the `page_path`
 * property instead of the event name.
 */
export const VACCINATION_CTA_EVENTS = {
  /** /vaccination */
  landed_on_main_page: "vaccination.main_page.navigation.landed_on_page_cta",
  /** /vaccination/mottagningar */
  landed_on_clinics_overview_page: "vaccination.clinics_overview_page.navigation.landed_on_page_cta",
  /** /vaccination/mottagningar/<clinic> */
  landed_on_clinic_page: "vaccination.clinic_page.navigation.landed_on_page_cta",
  /** "Boka tid" on a vaccine price card — main page and both vaccine overviews. */
  vaccine_card_book_cta_pressed: "vaccination.vaccine_card.navigation.book_cta_pressed",
  /** Clinic name link on the clinics overview, opening that clinic's own page. */
  clinic_detail_page_link_cta_clicked:
    "vaccination.clinics_overview_page.navigation.clinic_detail_page_link_cta_clicked",
  /** "Boka tid" on a clinic card in the clinics overview, opening the MittVaccin iframe. */
  book_cta_pressed: "vaccination.clinics_overview_page.navigation.book_cta_pressed",
  /** Floating CTA on a clinic page, opening the MittVaccin iframe. */
  book_floating_cta_clicked: "vaccination.clinic_page.navigation.book_floating_cta_clicked",
  /** Floating CTA on a vaccine page, going to the clinics overview filtered to that vaccine. */
  go_to_clinics_overview_page_floating_cta:
    "vaccination.vaccine_detail_page.navigation.go_to_clinics_overview_page_floating_cta",
  /** The same CTA on a travel vaccine page. */
  travel_vaccine_go_to_clinics_overview_page_floating_cta:
    "vaccination.travel_vaccine_detail_page.navigation.go_to_clinics_overview_page_floating_cta",
  /**
   * The same CTA again, on the three listing pages. Separate keys rather than one shared value:
   * the pages are what the analytics team compares, and a single event would collapse them.
   */
  main_page_go_to_clinics_overview_page_floating_cta:
    "vaccination.main_page.navigation.go_to_clinics_overview_page_floating_cta",
  vaccines_overview_go_to_clinics_overview_page_floating_cta:
    "vaccination.vaccines_overview_page.navigation.go_to_clinics_overview_page_floating_cta",
  travel_vaccines_overview_go_to_clinics_overview_page_floating_cta:
    "vaccination.travel_vaccines_overview_page.navigation.go_to_clinics_overview_page_floating_cta",
  /** /vaccination/foretag */
  landed_on_cooperation_partners_page: "vaccination.cooperation_partners_page.navigation.landed_on_page_cta"
} as const

export type VaccinationCtaEventKey = keyof typeof VACCINATION_CTA_EVENTS
